// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A table component for displaying fulfillment order information with filtering.
 */
import { EmptyState } from '@/components/empty-state';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import {
  ArrowOutwardRounded,
  InfoOutlined,
} from '@/constants/hardfin-standard-icons';
import { Sort } from '@/constants/sort';
import { getFilterStatuses } from '@/fulfillment/api/get-fulfillments';
import { FulfillmentFilter } from '@/fulfillment/api/types';
import { CYCLE_TIME_SEGMENTS } from '@/fulfillment/components/cycle-time-segments';
import { FulfillmentTableRow } from '@/fulfillment/components/fulfillment-table-row';
import {
  FulfillmentSortProperty,
  useFulfillments,
} from '@/fulfillment/hooks/use-fulfillments';
import { useTableSort } from '@/hooks/use-table-sort';
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

/**
 * Props for the FulfillmentTable component.
 */
interface Props {
  filter?: FulfillmentFilter;
  sort: Sort<FulfillmentSortProperty>;
  onSort: (property: FulfillmentSortProperty) => void;
  onPageChange: (pageNumber: number) => void;
  hidePageBar?: boolean;
  enableRowExpansion?: boolean;
}

/**
 * A table component for displaying fulfillment order information with filtering.
 */
export function FulfillmentTable(props: Props) {
  const { fulfillments, resultCount } = useFulfillments({
    filter: props.filter,
  });

  const downloadURL = new URL(
    '/v1/export/fulfillment-orders',
    import.meta.env.VITE_API_URL
  );
  const statuses = getFilterStatuses(
    props.filter?.status,
    props.filter?.subStatuses
  );
  if (statuses) {
    for (const s of statuses) {
      downloadURL.searchParams.append('statuses', s);
    }
  }
  if (props.filter?.searchTerm) {
    downloadURL.searchParams.set('search', props.filter.searchTerm);
  }
  // Forward the current project filter to the export so the generated xlsx is
  // scoped to the same project the operator sees on screen — including the
  // per-SKU summary sheet that only renders with a project filter applied.
  if (props.filter?.projectId) {
    downloadURL.searchParams.append('projectIds', props.filter.projectId);
  }

  const pageNumber = props.filter?.page ?? 1;
  const pageSize = props.filter?.pageSize ?? 20;

  const onPageChange = props.onPageChange;

  // Track which rows are expanded by fulfillment ID
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const handleToggleExpand = useCallback((fulfillmentId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(fulfillmentId)) {
        next.delete(fulfillmentId);
      } else {
        next.add(fulfillmentId);
      }
      return next;
    });
  }, []);

  /**
   * Table sort helpers for managing sort indicators.
   */
  const { getSortDirection, getLabelDirection, isLabelActive } = useTableSort(
    props.sort
  );

  /**
   * Creates a sort handler for a specific column.
   */
  const createSortHandler = useCallback(
    (property: FulfillmentSortProperty) => () => {
      props.onSort(property);
    },
    [props]
  );

  // Handle page overrun
  useEffect(() => {
    if (onPageChange && fulfillments.length === 0 && pageNumber > 1) {
      onPageChange(1);
    }
  }, [fulfillments.length, pageNumber, onPageChange]);

  if (fulfillments.length === 0) {
    return (
      <Paper variant="outlined">
        <EmptyState title="No fulfillment orders found">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography color="text.secondary">
              To start tracking fulfillment orders in Hardfin, add a new
              fulfillment order
            </Typography>
            <ArrowOutwardRounded fontSize="small" color="action" />
          </Stack>
        </EmptyState>
      </Paper>
    );
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                width={props.enableRowExpansion ? '21%' : '22%'}
                sortDirection={getSortDirection('foNumber')}
              >
                <TableSortLabel
                  active={isLabelActive('foNumber')}
                  direction={getLabelDirection('foNumber')}
                  onClick={createSortHandler('foNumber')}
                >
                  References
                </TableSortLabel>
              </TableCell>
              <TableCell width={props.enableRowExpansion ? '21%' : '22%'}>
                From/To location
              </TableCell>
              <TableCell width={props.enableRowExpansion ? '26%' : '27%'}>
                Items
              </TableCell>
              <TableCell
                width={props.enableRowExpansion ? '14%' : '14%'}
                sortDirection={getSortDirection('cycleTime')}
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <TableSortLabel
                    active={isLabelActive('cycleTime')}
                    direction={getLabelDirection('cycleTime')}
                    onClick={createSortHandler('cycleTime')}
                  >
                    Cycle time
                  </TableSortLabel>
                  <Tooltip
                    arrow
                    title={
                      <Stack spacing={0.75}>
                        {CYCLE_TIME_SEGMENTS.map((segment) => (
                          <Stack key={segment.key} spacing={0.25}>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600 }}
                            >
                              {segment.label}
                            </Typography>
                            <Typography variant="caption">
                              {segment.tooltip}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    }
                  >
                    <InfoOutlined
                      sx={{ fontSize: '1rem', color: 'text.secondary' }}
                    />
                  </Tooltip>
                </Stack>
              </TableCell>
              <TableCell
                width={props.enableRowExpansion ? '18%' : '15%'}
                sortDirection={getSortDirection('status')}
              >
                <TableSortLabel
                  active={isLabelActive('status')}
                  direction={getLabelDirection('status')}
                  onClick={createSortHandler('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              {props.enableRowExpansion && <TableCell width="48px" />}
            </TableRow>
          </TableHead>
          <TableBody>
            {fulfillments.map((fulfillment) => (
              <FulfillmentTableRow
                key={fulfillment.id}
                fulfillment={fulfillment}
                enableRowExpansion={props.enableRowExpansion}
                isExpanded={expandedRows.has(fulfillment.id)}
                onToggleExpand={() => handleToggleExpand(fulfillment.id)}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!props.hidePageBar && (
        <TablePaginationFooter
          pagination={{
            page: pageNumber,
            pageSize,
            onPageChange,
          }}
          totalCount={resultCount}
          entityOptions={{
            singular: 'fulfillment order',
            includeCount: false,
          }}
          downloadURL={downloadURL}
          downloadLabel="Export fulfillment order data"
        />
      )}
    </>
  );
}
